import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareAlt,
  faArrowCircleRight,
  faDownload,
  faUser,
  faLongArrowRight,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";

// Import Roboto font from Google Fonts (add this in your main HTML file)
// Alternatively, include this in your CSS: @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');

// --- STYLES DEFINITION (Refactored for Roboto font) ---

const styleDefinitions = {
  // Base Styles
  root: {
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    // *** CHANGED TO ROBOTO FONT FAMILY ***
    fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    // ***********************************

    fontWeight: 400, // Default Roboto weight
  },
  container: {
    width: "100%",
    height: "100%",
  },
  loadingErrorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "18px",
    color: "#666",
    flexDirection: "column",
    gap: "10px",
    fontFamily: '"Roboto", sans-serif',
  },
  projectDetailsTopBox: {
    display: "inline-grid",
    gridTemplateColumns: "60% 40%",
    gap: 0,
    width: "100%",
    overflow: "hidden",
    fontFamily: '"Roboto", sans-serif',
  },
  projectImageContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    minHeight: "400px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  projectMainImage: {
    width: "100%",
    height: "100%",
    backgroundColor: "#e0e0e0",
    objectFit: "cover",
    display: "",
  },
  reraBadge: {
    position: "absolute",
    top: "20px",
    right: "20px",
    backgroundColor: "#fff",
    color: "#cc3333",
    padding: "8px 15px",
    borderRadius: "50%",
    border: "3px solid #cc3333",
    fontWeight: "700", // Roboto bold
    textTransform: "uppercase",
    fontSize: "14px",
    transform: "rotate(15deg)",
    zIndex: 2,
    fontFamily: '"Roboto", sans-serif',
  },
  projectDetailsGrid: {
    color: "#fff",
    boxSizing: "border-box",
    backgroundColor: "#005f5f",
    width: "100%",
    padding: "35px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontFamily: '"Roboto", sans-serif',
  },
  highlightTitleWrap: {
    marginBottom: "20px",
  },
  highlightTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
    paddingBottom: "10px",
  },
  highlightTitleHeading: {
    fontSize: "20px",
    fontWeight: 700, // Roboto bold
    color: "#fff",
    fontFamily: '"Roboto", sans-serif',
    letterSpacing: "0.5px",
  },
  shareIconsPopover: {
    position: "relative",
  },
  uniqueShareBtn: {
    cursor: "pointer",
    fontSize: "20px",
    color: "#f4a542",
    padding: "8px",
    borderRadius: "50%",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    fontFamily: '"Roboto", sans-serif',
  },
  uniqueShareIconsBox: {
    position: "absolute",
    top: "100%",
    right: 0,
    padding: "10px",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    gap: "10px",
    flexWrap: "wrap",
    minWidth: "120px",
    display: "flex",
    fontFamily: '"Roboto", sans-serif',
  },
  uniqueShareIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    color: "#fff",
    borderRadius: "50%",
    fontSize: "16px",
    textDecoration: "none",
    transition: "transform 0.3s ease",
    fontFamily: '"Roboto", sans-serif',
  },
  fb: { backgroundColor: "#3b5998" },
  tw: { backgroundColor: "#00aced" },
  wh: { backgroundColor: "#25d366" },
  highlightList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 30px 0",
    maxHeight: "220px",
    overflowY: "auto",
    fontFamily: '"Roboto", sans-serif',
  },
  highlightListItem: {
    fontSize: "15px",
    marginBottom: "12px",
    color: "#fff",
    display: "flex",
    alignItems: "flex-start",
    lineHeight: 1.5, // Better line height for Roboto
    padding: "5px 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    fontFamily: '"Roboto", sans-serif',
    fontWeight: 400,
  },
  highlightListIcon: {
    color: "#f4a542",
    marginRight: "12px",
    marginTop: "3px",
    flexShrink: 0,
    fontSize: "14px",
  },
  progressDiv: {
    marginBottom: "30px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    padding: "20px",
    borderRadius: "8px",
    fontFamily: '"Roboto", sans-serif',
  },
  statusTitle: {
    fontSize: "16px",
    fontWeight: 600, // Semi-bold for Roboto
    marginBottom: "15px",
    color: "#fff",
    fontFamily: '"Roboto", sans-serif',
    letterSpacing: "0.3px",
  },
  progressContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "5px",
    height: "10px",
    overflow: "hidden",
    marginBottom: "10px",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#f4a542",
    borderRadius: "5px",
    transition: "width 0.5s ease-in-out",
  },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.8)",
    fontFamily: '"Roboto", sans-serif',
    fontWeight: 400,
  },
  projectStatus: {
    fontSize: "14px",
    textAlign: "center",
    marginTop: "8px",
    color: "#f4a542",
    textTransform: "capitalize",
    fontWeight: 600, // Semi-bold
    fontFamily: '"Roboto", sans-serif',
  },
  enquireBtn: {
    width: "100%",
    padding: "16px",
    marginBottom: "25px",
    backgroundColor: "#f4a542",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600, // Semi-bold
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    fontFamily: '"Roboto", sans-serif',
    letterSpacing: "0.5px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    fontFamily: '"Roboto", sans-serif',
  },
  actionButton: {
    flex: 1,
    padding: "14px 10px",
    border: "1px solid #f4a542",

    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600, // Semi-bold
    transition: "all 0.3s ease",
    backgroundColor: "transparent",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "48px",
    fontFamily: '"Roboto", sans-serif',
  },
  brochureBtn: {
    backgroundColor: "#f4a542",
    borderColor: "#f4a542",
    color: "#fff",
  },
  imageOverlay: {
    position: "absolute",
    top: 0,
    left: 2,
    objectFit: "cover",
    width: "100%",
    height: "100%",
    zIndex: 1,
  },

  // Media Query Styles
  "@media (max-width: 768px)": {
    projectDetailsTopBox: {
      gridTemplateColumns: "1fr",
      margin: "20px 0",
      fontFamily: '"Roboto", sans-serif',
    },
    projectDetailsGrid: {
      padding: "20px",
      fontFamily: '"Roboto", sans-serif',
    },
    highlightTitleHeading: {
      fontSize: "16px",
      fontWeight: 600,
      fontFamily: '"Roboto", sans-serif',
    },
    projectImageContainer: {
      minHeight: "300px",
    },
    reraBadge: {
      top: "10px",
      right: "10px",
      padding: "6px 12px",
      fontSize: "12px",
      fontFamily: '"Roboto", sans-serif',
    },
    uniqueShareIconsBox: {
      right: "-20px",
      fontFamily: '"Roboto", sans-serif',
    },
    highlightList: {
      maxHeight: "200px",
      fontFamily: '"Roboto", sans-serif',
    },
    progressDiv: {
      fontFamily: '"Roboto", sans-serif',
    },
    enquireBtn: {
      fontSize: "15px",
      fontFamily: '"Roboto", sans-serif',
    },
    actionButtons: {
      flexDirection: "column",
      gap: "10px",
      fontFamily: '"Roboto", sans-serif',
    },
    actionButton: {
      padding: "12px 10px",
      fontSize: "13px",
      fontFamily: '"Roboto", sans-serif',
    },
  },
  "@media (max-width: 992px) and (min-width: 769px)": {
    projectDetailsTopBox: {
      gridTemplateColumns: "1fr",
      fontFamily: '"Roboto", sans-serif',
    },
    projectImageContainer: {
      minHeight: "400px",
    },
  },
};

// Function to apply media queries
const useStyles = (styles, breakpoints) => {
  return useMemo(() => {
    const appliedStyles = {};
    for (const key in styles) {
      if (key.startsWith("@media")) continue;

      appliedStyles[key] = styles[key];

      if (
        breakpoints["max-width: 768px"] &&
        styles["@media (max-width: 768px)"] &&
        styles["@media (max-width: 768px)"][key]
      ) {
        appliedStyles[key] = {
          ...appliedStyles[key],
          ...styles["@media (max-width: 768px)"][key],
        };
      }
      if (
        breakpoints["max-width: 992px) and (min-width: 769px"] &&
        styles["@media (max-width: 992px) and (min-width: 769px)"] &&
        styles["@media (max-width: 992px) and (min-width: 769px)"][key]
      ) {
        appliedStyles[key] = {
          ...appliedStyles[key],
          ...styles["@media (max-width: 992px) and (min-width: 769px)"][key],
        };
      }
    }
    return appliedStyles;
  }, [styles, breakpoints]);
};

const MithaHaritham = () => {
  const [breakpoints, setBreakpoints] = useState({
    "max-width: 768px": window.innerWidth <= 768,
    "max-width: 992px) and (min-width: 769px":
      window.innerWidth > 768 && window.innerWidth <= 992,
  });

  useEffect(() => {
    const handleResize = () => {
      setBreakpoints({
        "max-width: 768px": window.innerWidth <= 768,
        "max-width: 992px) and (min-width: 769px":
          window.innerWidth > 768 && window.innerWidth <= 992,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const styles = useStyles(styleDefinitions, breakpoints);
  const params = useParams();
  const projectId =
    params.projectId || params.id || params.projectID || params.project_id;

  const [shareOpen, setShareOpen] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const IMG_BASE_URL = "https://dev.mithahomes.com/backend/";

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) {
        setError("No project ID found in URL");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://dev.mithahomes.com/backend/api/mainprojectprocess/projectsgetapi/${projectId}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();

        if (data.success) {
          setProjectData(data.data);
        } else {
          throw new Error(data.message || "API returned unsuccessful response");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  const getProgressPercentage = (status) => {
    if (!status) return 50;

    // Handle different types of status
    const statusString = String(status).trim();

    // Check if it's a percentage (contains % or just numbers)
    const percentageMatch = statusString.match(/(\d+\.?\d*)%/);
    if (percentageMatch) {
      const percentage = parseFloat(percentageMatch[1]);
      if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
        return Math.min(100, Math.max(0, percentage));
      }
    }

    // Check if it's just a number
    const pureNumber = parseFloat(statusString);
    if (!isNaN(pureNumber) && pureNumber >= 0 && pureNumber <= 100) {
      return Math.min(100, Math.max(0, pureNumber));
    }

    // Check for text-based statuses with percentage
    const textWithPercentage = statusString.toLowerCase();
    const textPercentageMatch = textWithPercentage.match(/(\d+)\s*percent/);
    if (textPercentageMatch) {
      const percentage = parseInt(textPercentageMatch[1], 10);
      if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
        return Math.min(100, Math.max(0, percentage));
      }
    }

    // Original logic for text statuses
    const statusMap = {
      planning: 10,
      foundation: 25,
      construction: 50,
      finishing: 75,
      completed: 100,
      handover: 100,
      launched: 5,
      "sold out": 100,
      "ready to move": 100,
      "under construction": 50,
    };

    const lowercaseStatus = statusString.toLowerCase();
    return statusMap[lowercaseStatus] || 50;
  };

  const getStatusDisplayText = (status) => {
    if (!status) return "In Progress";

    const statusString = String(status).trim();

    // If it's already a percentage format, keep it as is
    if (statusString.match(/^\d+%?$/)) {
      return statusString.includes("%") ? statusString : `${statusString}%`;
    }

    // If it's a number
    const pureNumber = parseFloat(statusString);
    if (!isNaN(pureNumber) && pureNumber >= 0 && pureNumber <= 100) {
      return `${pureNumber}%`;
    }

    // Check for text with percentage
    const percentageMatch = statusString.match(/(\d+\.?\d*)%/);
    if (percentageMatch) {
      return percentageMatch[0];
    }

    // For text statuses, return as is
    return statusString;
  };

  const formatHighlights = (highlights) => {
    if (!highlights || highlights.length === 0) return [];

    if (Array.isArray(highlights) && highlights[0]?.text) {
      return highlights.slice(0, 5).map((item) => item.text.trim());
    }

    if (typeof highlights === "string") {
      const points = highlights
        .split(/\.\s+|;\s+|\n+/)
        .filter((point) => point.trim().length > 0);
      return points.slice(0, 5).map((point) => point.trim());
    }

    if (Array.isArray(highlights)) {
      return highlights.slice(0, 5);
    }

    return [];
  };

  const formatHighContent = (highcontent) => {
    if (!highcontent || !Array.isArray(highcontent)) return [];

    // Extract values from highcontent array
    return highcontent
      .filter((item) => item && item.value)
      .slice(0, 5)
      .map((item) => item.value.trim());
  };

  // Combine highlights and highcontent
  const getCombinedHighlights = () => {
    const highlightsList = formatHighlights(projectData?.highlights || []);
    const highContentList = formatHighContent(projectData?.highcontent || []);

    // If we have both, combine them (prioritize highcontent first)
    if (highContentList.length > 0) {
      return highContentList;
    }

    // Fallback to highlights if no highcontent
    if (highlightsList.length > 0) {
      return highlightsList;
    }

    // Default fallback highlights
    return [
      "DTCP Approved",
      "Home loan available from all leading banks",
      "On-time flat handover",
      "Member of CREDAI",
      "Best value for your investment",
    ];
  };

  const handleBrochureClick = () => {
    if (projectData?.brochure) {
      // If brochure is a URL, open it
      window.open(`${IMG_BASE_URL}${projectData.brochure}`, "_blank");
    } else {
      // If brochure is null, show message or disable button
      alert("Brochure not available at the moment.");
    }
  };

  const handleVideoClick = () => {
    if (projectData?.video) {
      // If video is a URL, open it
      if (projectData.video.startsWith("http")) {
        window.open(projectData.video, "_blank");
      } else {
        window.open(`${IMG_BASE_URL}${projectData.video}`, "_blank");
      }
    } else {
      // If video is null, show message or disable button
      alert("Project video not available at the moment.");
    }
  };

  if (loading) {
    return (
      <div style={styles.root}>
        <div style={styles.container}>
          <div style={styles.loadingErrorContainer}>
            <div>Loading project details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.root}>
        <div style={styles.container}>
          <div style={styles.loadingErrorContainer}>
            <div>Error loading project: {error}</div>
            <div>Extracted Project ID: {projectId || "Not found"}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div style={styles.root}>
        <div style={styles.container}>
          <div style={styles.loadingErrorContainer}>
            <div>No project data available</div>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = getProgressPercentage(projectData.project_status);
  const statusDisplayText = getStatusDisplayText(projectData.project_status);
  const combinedHighlights = getCombinedHighlights();

  return (
    <div style={styles.root}>
      <div style={styles.container}>
        <div style={styles.projectDetailsTopBox}>
          <div style={styles.projectImageContainer}>
            <img
              alt={
                projectData.project_name ||
                "Mitha Haritham Residential Apartments"
              }
              src={
                `${IMG_BASE_URL}${projectData.cover_image}` ||
                "https://via.placeholder.com/800x500/3cb371/FFFFFF?text=Project+Image"
              }
              style={styles.projectMainImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/800x500/3cb371/FFFFFF?text=Project+Image";
              }}
            />
            <div style={styles.imageOverlay}></div>
            {projectData.rera_number && (
              <div style={styles.reraBadge}>RERA</div>
            )}
          </div>

          <div
            style={{
              backgroundColor: "#2b5f5f",
              padding: "25px",
              color: "#fff",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            {/* ===== TITLE + SHARE ===== */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                {projectData.project_name
                  ? `${projectData.project_name} HIGHLIGHTS`
                  : "PROJECT HIGHLIGHTS"}
              </h2>
            </div>

            {/* ===== HIGHLIGHTS LIST ===== */}
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {combinedHighlights.map((item, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "10px",
                    fontSize: "15px",
                  }}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                  {item}
                </li>
              ))}
            </ul>

            {/* ===== CONSTRUCTION STATUS ===== */}
            <div style={{ marginTop: "25px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "10px",
                }}
              >
                Construction Current Status
              </h3>

              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#d9d9d9",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progressPercentage}%`,
                    height: "100%",
                    backgroundColor: "#f4a542",
                    transition: "width 0.4s ease",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "13px",
                  marginTop: "6px",
                }}
              >
                <span>0%</span>
                <span>{progressPercentage}%</span>
              </div>

              {/* Display the project status text */}

              {/* Additional status description for non-percentage statuses */}
              {projectData.project_status &&
                !String(projectData.project_status).match(/^\d+%?$/) &&
                isNaN(parseFloat(projectData.project_status)) && (
                  <div
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      marginTop: "4px",
                      color: "rgba(255, 255, 255, 0.7)",
                      fontStyle: "italic",
                    }}
                  >
                    Progress: {progressPercentage}%
                  </div>
                )}
            </div>

            {/* ===== ENQUIRE BUTTON ===== */}
            <button
              style={{
                marginTop: "20px",
                color: "#f5a623",
                background: "transparent",
                border: "none",
                fontSize: "16px",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#f5a623";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              ENQUIRE NOW
              <FontAwesomeIcon icon={faArrowCircleRight} />
            </button>

            {/* ===== ACTION BUTTONS ===== */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "15px",
                flexWrap: "wrap",
              }}
            >
              {/* BROCHURE */}
              <button
                style={{
                  backgroundColor: projectData.brochure ? "#e3702c" : "#cccccc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: projectData.brochure ? "pointer" : "not-allowed",
                  transition: "all 0.3s ease",
                  opacity: projectData.brochure ? 1 : 0.7,
                }}
                onClick={handleBrochureClick}
                disabled={!projectData.brochure}
                onMouseEnter={(e) => {
                  if (projectData.brochure) {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.color = "#000";
                  }
                }}
                onMouseLeave={(e) => {
                  if (projectData.brochure) {
                    e.currentTarget.style.backgroundColor = "#e3702c";
                    e.currentTarget.style.color = "#fff";
                  }
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
                Brochure
              </button>

              {/* VIDEO */}
              <button
                style={{
                  backgroundColor: projectData.video ? "#f4a542" : "#cccccc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: projectData.video ? "pointer" : "not-allowed",
                  transition: "all 0.3s ease",
                  opacity: projectData.video ? 1 : 0.7,
                }}
                onClick={handleVideoClick}
                disabled={!projectData.video}
                onMouseEnter={(e) => {
                  if (projectData.video) {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.color = "#000";
                  }
                }}
                onMouseLeave={(e) => {
                  if (projectData.video) {
                    e.currentTarget.style.backgroundColor = "#f4a542";
                    e.currentTarget.style.color = "#fff";
                  }
                }}
              >
                <FontAwesomeIcon icon={faUser} />
                Project Video
              </button>

              {/* DIRECTION */}
              <button
                style={{
                  backgroundColor: projectData.get_direction ? "#333" : "red",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: projectData.get_direction ? "pointer" : "not-allowed",
                  transition: "all 0.3s ease",
                  opacity: projectData.get_direction ? 1 : 0.7,
                }}
                onClick={() =>
                  projectData.get_direction &&
                  window.open(projectData.get_direction, "_blank")
                }
                disabled={!projectData.get_direction}
                onMouseEnter={(e) => {
                  if (projectData.get_direction) {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.color = "#000";
                  }
                }}
                onMouseLeave={(e) => {
                  if (projectData.get_direction) {
                    e.currentTarget.style.backgroundColor = "#333";
                    e.currentTarget.style.color = "#fff";
                  }
                }}
              >
                <FontAwesomeIcon icon={faLongArrowRight} />
                Get Direction
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MithaHaritham;
